import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const FoundationPageContent = ({ isDarkMode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="foundation-paaf" ref={sectionRef} className={`py-28 relative overflow-hidden ${isDarkMode ? 'bg-[#111A19]' : 'bg-[#E5D9B6]'}`}>
      {/* Animated background rings */}
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border rounded-full animate-scale-pulse opacity-50 ${isDarkMode ? 'border-[#284139]/20' : 'border-[#284139]/10'}`} />
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border rounded-full animate-scale-pulse opacity-50 ${isDarkMode ? 'border-[#284139]/20' : 'border-[#284139]/10'}`} style={{ animationDelay: '0.5s' }} />
      <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border rounded-full animate-scale-pulse opacity-50 ${isDarkMode ? 'border-[#E5D9B6]/10' : 'border-[#284139]/15'}`} style={{ animationDelay: '1s' }} />
      
      {/* Background decorations */}
      <div className={`absolute top-1/2 -translate-y-1/2 right-0 w-[700px] h-[700px] rounded-full blur-[120px] ${isDarkMode ? 'bg-[#284139]/15' : 'bg-[#284139]/10'}`} />
      <div className={`absolute top-0 left-1/4 w-[400px] h-[400px] rounded-full blur-[100px] ${isDarkMode ? 'bg-[#E5D9B6]/5' : 'bg-[#284139]/5'}`} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className={`inline-flex items-center gap-2 text-sm font-semibold tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full transition-colors cursor-default ${
            isDarkMode 
              ? 'text-[#E5D9B6] bg-[#E5D9B6]/10 border border-[#E5D9B6]/20 hover:bg-[#E5D9B6]/20'
              : 'text-[#284139] bg-[#284139]/10 border border-[#284139]/20 hover:bg-[#284139]/20'
          }`}>
            <Sparkles size={14} className="animate-pulse" />
            Scholarship Program
          </span>
          <h2 className={`font-display text-4xl lg:text-5xl font-bold mb-3 leading-tight ${isDarkMode ? 'text-[#f5f5f5]' : 'text-[#284139]'}`}>
            PATHWAYS AFRICA<br />AIDED FUND (PAAF)
          </h2>
          
          <div className={`w-24 h-1.5 rounded-full mx-auto mt-6 ${isDarkMode ? 'bg-[#E5D9B6]' : 'bg-[#284139]'}`} />
        </div>

        {/* Main Content Card */}
        <div className={`relative rounded-3xl p-10 lg:p-14 mb-12 overflow-hidden ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#1a2625] to-[#111A19] border border-[#284139]'
            : 'bg-gradient-to-br from-[#284139] to-[#1a2625] border border-[#284139]'
        }`}>
          {/* Decorative corner accent */}
          <div className={`absolute top-0 right-0 w-64 h-64 rounded-bl-full ${isDarkMode ? 'bg-gradient-to-bl from-[#E5D9B6]/10 to-transparent' : 'bg-gradient-to-bl from-[#E5D9B6]/20 to-transparent'}`} />
          
          <div className="max-w-4xl mx-auto relative z-10">
            <p className={`text-lg leading-relaxed mb-6 ${isDarkMode ? 'text-[#809076]' : 'text-[#E5D9B6]/90'}`}>
              Our Premium College Counselling Program designed to guide high-achieving African students through the competitive college admissions process. This scholarship is intended for motivated students who have demonstrated academic excellence, leadership potential, and a clear drive to pursue higher education, but who may not have access to expert admissions support.
            </p>
            
            <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-[#809076]' : 'text-[#E5D9B6]/90'}`}>
              Please complete this application thoughtfully and honestly. Your responses will help us assess your background, aspirations, and readiness to benefit from the opportunities offered through Pathways Africa. All information submitted will be kept confidential and used solely for scholarship selection purposes.
            </p>
          </div>
        </div>

        {/* Applications Coming Soon */}
        <div className="text-center">
          <div className={`inline-flex items-center gap-3 px-8 py-5 rounded-2xl border ${
            isDarkMode 
              ? 'bg-[#284139]/30 border-[#E5D9B6]/30 text-[#E5D9B6]'
              : 'bg-[#284139]/20 border-[#284139]/30 text-[#284139]'
          }`}>
            <Sparkles size={20} className="animate-pulse" />
            <span className="font-semibold text-lg">Applications open soon</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FoundationPageContent;
